import { baseApi } from "../../../app/api";
import type {
  GetOrderParams,
  TransformedOrder,
  TransformedOrderItem,
  TransformedOrderCoupon,
  TransformedOrderTotals,
} from "../types/orderApiTypes";
import type {
  RawOrder,
  RawOrderItem,
  RawOrderCoupon,
  RawOrderTotals,
} from "../../../types/api/wcStoreApi/wcStoreApiOrderEndpointTypes";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { formatMinorUnitString } from "../../../common/helpers/formatPrice";
import { transformAddress } from "../../cart/api/cartApi";

// --- Base URL Constants ---
const WC_STORE_API_BASE =
  window.themeVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

// --- Transformation Functions ---
const transformOrderItem = (
  rawItem: RawOrderItem,
  currencyMinorUnit: number
): TransformedOrderItem => {
  const formattedLineTotal = formatMinorUnitString(
    rawItem.totals.line_total,
    currencyMinorUnit
  );
  const formattedLineSubtotal = formatMinorUnitString(
    rawItem.totals.line_subtotal,
    currencyMinorUnit
  );

  return {
    key: rawItem.key,
    id: rawItem.id,
    name: rawItem.name,
    quantity: rawItem.quantity,
    sku: rawItem.sku,
    permalink: rawItem.permalink,
    images: rawItem.images || [],
    price: rawItem.prices.price,
    lineTotal: rawItem.totals.line_total,
    formattedLineTotal: formattedLineTotal,
    lineSubtotal: rawItem.totals.line_subtotal, // Add raw subtotal
    formattedLineSubtotal: formattedLineSubtotal, // Add formatted subtotal
    variation: rawItem.variation || [],
  };
};

const transformOrderCoupon = (
  rawCoupon: RawOrderCoupon,
  currencyMinorUnit: number
): TransformedOrderCoupon => {
  const formattedDiscount = formatMinorUnitString(
    rawCoupon.totals.total_discount,
    currencyMinorUnit
  );

  return {
    code: rawCoupon.code,
    discountAmount: rawCoupon.totals.total_discount,
    formattedDiscountAmount: formattedDiscount,
  };
};

const transformOrderTotals = (
  rawTotals: RawOrderTotals,
  currencyMinorUnit: number
): TransformedOrderTotals => {
  const divisor = Math.pow(10, currencyMinorUnit);
  return {
    subtotal: parseFloat(rawTotals.subtotal) / divisor,
    totalDiscount: parseFloat(rawTotals.total_discount) / divisor,
    totalShipping: parseFloat(rawTotals.total_shipping) / divisor,
    totalFees: parseFloat(rawTotals.total_fees) / divisor,
    totalTax: parseFloat(rawTotals.total_tax) / divisor,
    totalRefund: parseFloat(rawTotals.total_refund) / divisor,
    totalPrice: parseFloat(rawTotals.total_price) / divisor,
    totalItems: parseFloat(rawTotals.total_items) / divisor,
    totalItemsTax: parseFloat(rawTotals.total_items_tax) / divisor,
    totalFeesTax: parseFloat(rawTotals.total_fees_tax) / divisor,
    totalDiscountTax: parseFloat(rawTotals.total_discount_tax) / divisor,
    totalShippingTax: parseFloat(rawTotals.total_shipping_tax) / divisor,
    taxLines: (rawTotals.tax_lines || []).map((line) => ({
      name: line.name,
      price: parseFloat(line.price) / divisor,
      formattedPrice: formatMinorUnitString(line.price, currencyMinorUnit),
    })),
    currencyCode: rawTotals.currency_code,
    currencySymbol: rawTotals.currency_symbol,
    currencyMinorUnit: rawTotals.currency_minor_unit,
    currencyDecimalSeparator: rawTotals.currency_decimal_separator,
    currencyThousandSeparator: rawTotals.currency_thousand_separator,
    currencyPrefix: rawTotals.currency_prefix,
    currencySuffix: rawTotals.currency_suffix,
    formattedSubtotal: formatMinorUnitString(
      rawTotals.subtotal,
      currencyMinorUnit
    ),
    formattedTotalDiscount: formatMinorUnitString(
      rawTotals.total_discount,
      currencyMinorUnit
    ),
    formattedTotalShipping: formatMinorUnitString(
      rawTotals.total_shipping,
      currencyMinorUnit
    ),
    formattedTotalFees: formatMinorUnitString(
      rawTotals.total_fees,
      currencyMinorUnit
    ),
    formattedTotalTax: formatMinorUnitString(
      rawTotals.total_tax,
      currencyMinorUnit
    ),
    formattedTotalRefund: formatMinorUnitString(
      rawTotals.total_refund,
      currencyMinorUnit
    ),
    formattedTotalPrice: formatMinorUnitString(
      rawTotals.total_price,
      currencyMinorUnit
    ),
    formattedTotalItems: formatMinorUnitString(
      rawTotals.total_items,
      currencyMinorUnit
    ),
  };
};

const transformOrder = (rawOrder: RawOrder): TransformedOrder => {
  const currencyMinorUnit = rawOrder.totals.currency_minor_unit;
  const transformedTotals = transformOrderTotals(
    rawOrder.totals,
    currencyMinorUnit
  );

  return {
    id: rawOrder.id,
    status: rawOrder.status,
    coupons:
      rawOrder.coupons?.map((c) =>
        transformOrderCoupon(c, currencyMinorUnit)
      ) || [],
    shippingAddress: transformAddress(rawOrder.shipping_address),
    billingAddress: transformAddress(rawOrder.billing_address),
    items:
      rawOrder.items?.map((item) =>
        transformOrderItem(item, currencyMinorUnit)
      ) || [],
    needsPayment: rawOrder.needs_payment,
    needsShipping: rawOrder.needs_shipping,
    totals: transformedTotals,
    currencyCode: transformedTotals.currencyCode,
    currencySymbol: transformedTotals.currencySymbol,
  };
};

export const orderApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrder: builder.query<TransformedOrder, GetOrderParams>({
      query: ({ orderId, key, billingEmail }) => {
        const params = new URLSearchParams();
        params.append("key", key);
        if (billingEmail) {
          params.append("billing_email", billingEmail);
        }
        return `${WC_STORE_API_BASE}/order/${orderId}?${params.toString()}`;
      },
      transformResponse: (response: RawOrder): TransformedOrder => {
        return transformOrder(response);
      },
      transformErrorResponse: (
        response: FetchBaseQueryError
      ): FetchBaseQueryError => {
        console.error("Error fetching order:", response);
        return response;
      },
      providesTags: (result, error, { orderId }) =>
        result ? [{ type: "Order", id: orderId }] : [],
    }),
  }),
});

export const { useGetOrderQuery } = orderApi;
